import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { auroraTopbarSx } from '../theme/auroraSurface';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton,
  ListItem, ListItemButton, ListItemIcon, ListItemText, CssBaseline,
  Avatar, Menu, MenuItem, Badge, Container, Grid, Card, CardContent,
  Paper, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Collapse, FormControlLabel, Switch, InputAdornment,
  Tooltip, useTheme, useMediaQuery, TablePagination, FormControl, InputLabel,
  Select, CircularProgress, Autocomplete
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, Business, People, Event, Settings,
  Notifications, Search, AccountCircle, ChevronLeft, ChevronRight,
  Add, Edit, Block, CheckCircle, PersonAdd, Logout, School,
  CalendarToday, Groups, Refresh, Person, ExpandMore, ExpandLess,
  Sell, AdminPanelSettings
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../utils/api';
import ThemeToggle from '../components/ThemeToggle';
import { INDIAN_CITIES } from '../utils/indianCities';
import { AuthContext } from '../context/AuthContext';
import { GRADIENTS, RADIUS } from '../theme/tokens';

const drawerWidth = 280;

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

function SalesDashboardPage() {
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Layout state
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [currentView, setCurrentView] = useState('dashboard');
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  // Data state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [libraries, setLibraries] = useState([]);
  const [librarians, setLibrarians] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [instituteManagers, setInstituteManagers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Library state
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryPage, setLibraryPage] = useState(0);
  const [libraryRowsPerPage, setLibraryRowsPerPage] = useState(10);
  const [libraryTotal, setLibraryTotal] = useState(0);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [createLibraryOpen, setCreateLibraryOpen] = useState(false);
  const [editLibraryOpen, setEditLibraryOpen] = useState(false);
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [newLibrary, setNewLibrary] = useState({ name: '', address: '', capacity: '', city: null, website_enabled: false });
  const [editLibraryData, setEditLibraryData] = useState({ name: '', address: '', capacity: '', city: null, website_enabled: false });

  // Librarian state
  const [librarianLoading, setLibrarianLoading] = useState(false);
  const [createLibrarianOpen, setCreateLibrarianOpen] = useState(false);
  const [assignLibrarianOpen, setAssignLibrarianOpen] = useState(false);
  const [selectedLibrarian, setSelectedLibrarian] = useState(null);
  const [newLibrarian, setNewLibrarian] = useState({ name: '', email: '', password: '', phone: '', library_id: '' });
  const [librarySearchOptions, setLibrarySearchOptions] = useState([]);
  const [librarySearchLoading, setLibrarySearchLoading] = useState(false);
  const [selectedAssignLibrary, setSelectedAssignLibrary] = useState(null);

  // Institute state
  const [instituteSearch, setInstituteSearch] = useState('');
  const [institutePage, setInstitutePage] = useState(0);
  const [instituteRowsPerPage, setInstituteRowsPerPage] = useState(10);
  const [instituteTotal, setInstituteTotal] = useState(0);
  const [instituteLoading, setInstituteLoading] = useState(false);
  const [createInstituteOpen, setCreateInstituteOpen] = useState(false);
  const [editInstituteOpen, setEditInstituteOpen] = useState(false);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [newInstitute, setNewInstitute] = useState({ name: '', address: '', city: null, contact_email: '' });
  const [editInstituteData, setEditInstituteData] = useState({ name: '', address: '', city: null, contact_email: '' });

  // Institute Manager state
  const [instituteManagerLoading, setInstituteManagerLoading] = useState(false);
  const [createInstituteManagerOpen, setCreateInstituteManagerOpen] = useState(false);
  const [assignInstituteManagerOpen, setAssignInstituteManagerOpen] = useState(false);
  const [selectedInstituteManager, setSelectedInstituteManager] = useState(null);
  const [newInstituteManager, setNewInstituteManager] = useState({ name: '', email: '', password: '', phone: '', city: '', institute_id: '' });
  const [instituteSearchOptions, setInstituteSearchOptions] = useState([]);
  const [instituteSearchLoading, setInstituteSearchLoading] = useState(false);
  const [selectedAssignInstitute, setSelectedAssignInstitute] = useState(null);

  // Bookings state
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingStatus, setBookingStatus] = useState('');
  const [bookingPage, setBookingPage] = useState(0);
  const [bookingRowsPerPage, setBookingRowsPerPage] = useState(20);
  const [bookingTotal, setBookingTotal] = useState(0);
  const [bookingLoading, setBookingLoading] = useState(false);

  // Sidebar sections state
  const [expandedSections, setExpandedSections] = useState({
    'library-section': true,
    'institute-section': true,
    'booking-section': true,
  });

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const menuSections = [
    {
      id: 'main',
      items: [
        { id: 'dashboard', text: 'Dashboard', icon: <Dashboard /> },
      ]
    },
    {
      id: 'library-section',
      label: 'Library Management',
      icon: <Business />,
      items: [
        { id: 'libraries', text: 'Libraries', icon: <Business /> },
        { id: 'librarians', text: 'Librarians', icon: <Person /> },
      ]
    },
    {
      id: 'institute-section',
      label: 'Institute Management',
      icon: <School />,
      items: [
        { id: 'institutes', text: 'Institutes', icon: <School /> },
        { id: 'institute-managers', text: 'Managers', icon: <AdminPanelSettings /> },
      ]
    },
    {
      id: 'booking-section',
      label: 'Bookings',
      icon: <Event />,
      items: [
        { id: 'bookings', text: 'All Bookings', icon: <Event /> },
      ]
    },
  ];

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch dashboard stats
  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sales/dashboard');
      setDashboardStats(response.data.data.stats);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      showSnackbar('Failed to load dashboard', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch libraries
  const fetchLibraries = async () => {
    try {
      setLibraryLoading(true);
      const params = new URLSearchParams({ page: libraryPage + 1, limit: libraryRowsPerPage });
      if (librarySearch) params.append('search', librarySearch);
      const response = await api.get(`/sales/libraries?${params}`);
      setLibraries(response.data.data.libraries || []);
      setLibraryTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching libraries:', err);
      showSnackbar('Failed to load libraries', 'error');
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'libraries') fetchLibraries();
  }, [currentView, libraryPage, libraryRowsPerPage]);

  // Fetch librarians
  const fetchLibrarians = async () => {
    try {
      setLibrarianLoading(true);
      const response = await api.get('/sales/librarians');
      setLibrarians(response.data.data.librarians || []);
    } catch (err) {
      console.error('Error fetching librarians:', err);
      showSnackbar('Failed to load librarians', 'error');
    } finally {
      setLibrarianLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'librarians') fetchLibrarians();
  }, [currentView]);

  // Fetch institutes
  const fetchInstitutes = async () => {
    try {
      setInstituteLoading(true);
      const params = new URLSearchParams({ page: institutePage + 1, limit: instituteRowsPerPage });
      if (instituteSearch) params.append('search', instituteSearch);
      const response = await api.get(`/sales/institutes?${params}`);
      setInstitutes(response.data.data.institutes || []);
      setInstituteTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching institutes:', err);
      showSnackbar('Failed to load institutes', 'error');
    } finally {
      setInstituteLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'institutes') fetchInstitutes();
  }, [currentView, institutePage, instituteRowsPerPage]);

  // Fetch institute managers
  const fetchInstituteManagers = async () => {
    try {
      setInstituteManagerLoading(true);
      const response = await api.get('/sales/institute-managers');
      setInstituteManagers(response.data.data.managers || []);
    } catch (err) {
      console.error('Error fetching institute managers:', err);
      showSnackbar('Failed to load institute managers', 'error');
    } finally {
      setInstituteManagerLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'institute-managers') fetchInstituteManagers();
  }, [currentView]);

  // Fetch bookings
  const fetchBookings = async () => {
    try {
      setBookingLoading(true);
      const params = new URLSearchParams({ page: bookingPage + 1, limit: bookingRowsPerPage });
      if (bookingSearch) params.append('search', bookingSearch);
      if (bookingStatus) params.append('status', bookingStatus);
      const response = await api.get(`/sales/bookings?${params}`);
      setBookings(response.data.data.bookings || []);
      setBookingTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      showSnackbar('Failed to load bookings', 'error');
    } finally {
      setBookingLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'bookings') fetchBookings();
  }, [currentView, bookingPage, bookingRowsPerPage, bookingStatus]);

  // Search libraries for assignment dropdown
  const searchLibrariesForAssignment = async (searchText) => {
    try {
      setLibrarySearchLoading(true);
      const params = new URLSearchParams({ limit: 50 });
      if (searchText) params.append('search', searchText);
      const response = await api.get(`/sales/libraries?${params}`);
      setLibrarySearchOptions(response.data.data.libraries || []);
    } catch (err) {
      console.error('Error searching libraries:', err);
    } finally {
      setLibrarySearchLoading(false);
    }
  };

  // Search institutes for assignment dropdown
  const searchInstitutesForAssignment = async (searchText) => {
    try {
      setInstituteSearchLoading(true);
      const params = new URLSearchParams({ limit: 50 });
      if (searchText) params.append('search', searchText);
      const response = await api.get(`/sales/institutes?${params}`);
      setInstituteSearchOptions(response.data.data.institutes || []);
    } catch (err) {
      console.error('Error searching institutes:', err);
    } finally {
      setInstituteSearchLoading(false);
    }
  };

  useEffect(() => {
    if (assignLibrarianOpen) {
      searchLibrariesForAssignment('');
      if (selectedLibrarian?.library) {
        setSelectedAssignLibrary(selectedLibrarian.library);
      } else {
        setSelectedAssignLibrary(null);
      }
    }
  }, [assignLibrarianOpen, selectedLibrarian]);

  useEffect(() => {
    if (assignInstituteManagerOpen) {
      searchInstitutesForAssignment('');
      if (selectedInstituteManager?.institute) {
        setSelectedAssignInstitute(selectedInstituteManager.institute);
      } else {
        setSelectedAssignInstitute(null);
      }
    }
  }, [assignInstituteManagerOpen, selectedInstituteManager]);

  // Library handlers
  const handleCreateLibrary = async () => {
    try {
      await api.post('/sales/libraries', {
        name: newLibrary.name,
        address: newLibrary.address,
        capacity: parseInt(newLibrary.capacity, 10) || 0,
        city: newLibrary.city || null,
        website_enabled: newLibrary.website_enabled,
      });
      setCreateLibraryOpen(false);
      setNewLibrary({ name: '', address: '', capacity: '', city: null, website_enabled: false });
      showSnackbar('Library created successfully');
      fetchLibraries();
      fetchDashboardStats();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create library', 'error');
    }
  };

  const handleEditLibrary = async () => {
    try {
      await api.put(`/sales/libraries/${selectedLibrary.id}`, {
        name: editLibraryData.name,
        address: editLibraryData.address,
        capacity: parseInt(editLibraryData.capacity, 10) || 0,
        city: editLibraryData.city || null,
        website_enabled: editLibraryData.website_enabled,
      });
      setEditLibraryOpen(false);
      setSelectedLibrary(null);
      showSnackbar('Library updated successfully');
      fetchLibraries();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update library', 'error');
    }
  };

  const openEditLibraryDialog = (library) => {
    setSelectedLibrary(library);
    setEditLibraryData({
      name: library.name,
      address: library.address,
      capacity: library.capacity || '',
      city: library.city || null,
      website_enabled: library.website_enabled || false,
    });
    setEditLibraryOpen(true);
  };

  // Librarian handlers
  const handleCreateLibrarian = async () => {
    try {
      await api.post('/sales/librarians', {
        name: newLibrarian.name,
        email: newLibrarian.email,
        password: newLibrarian.password,
        phone: newLibrarian.phone || null,
        library_id: newLibrarian.library_id || null,
      });
      setCreateLibrarianOpen(false);
      setNewLibrarian({ name: '', email: '', password: '', phone: '', library_id: '' });
      showSnackbar('Librarian created successfully');
      fetchLibrarians();
      fetchDashboardStats();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create librarian', 'error');
    }
  };

  const handleAssignLibrarian = async () => {
    try {
      await api.put(`/sales/librarians/${selectedLibrarian.id}/assign`, {
        library_id: selectedAssignLibrary?.id || null,
      });
      setAssignLibrarianOpen(false);
      setSelectedLibrarian(null);
      setSelectedAssignLibrary(null);
      showSnackbar('Librarian assignment updated');
      fetchLibrarians();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to assign librarian', 'error');
    }
  };

  // Institute handlers
  const handleCreateInstitute = async () => {
    try {
      await api.post('/sales/institutes', {
        name: newInstitute.name,
        address: newInstitute.address,
        city: newInstitute.city || null,
        contact_email: newInstitute.contact_email || null,
      });
      setCreateInstituteOpen(false);
      setNewInstitute({ name: '', address: '', city: null, contact_email: '' });
      showSnackbar('Institute created successfully');
      fetchInstitutes();
      fetchDashboardStats();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create institute', 'error');
    }
  };

  const handleEditInstitute = async () => {
    try {
      await api.put(`/sales/institutes/${selectedInstitute.id}`, {
        name: editInstituteData.name,
        address: editInstituteData.address,
        city: editInstituteData.city || null,
        contact_email: editInstituteData.contact_email || null,
      });
      setEditInstituteOpen(false);
      setSelectedInstitute(null);
      showSnackbar('Institute updated successfully');
      fetchInstitutes();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update institute', 'error');
    }
  };

  const openEditInstituteDialog = (institute) => {
    setSelectedInstitute(institute);
    setEditInstituteData({
      name: institute.name,
      address: institute.address,
      city: institute.city || null,
      contact_email: institute.contact_email || '',
    });
    setEditInstituteOpen(true);
  };

  // Institute Manager handlers
  const handleCreateInstituteManager = async () => {
    try {
      await api.post('/sales/institute-managers', {
        name: newInstituteManager.name,
        email: newInstituteManager.email,
        password: newInstituteManager.password,
        phone: newInstituteManager.phone || null,
        city: newInstituteManager.city || null,
        institute_id: newInstituteManager.institute_id || null,
      });
      setCreateInstituteManagerOpen(false);
      setNewInstituteManager({ name: '', email: '', password: '', phone: '', city: '', institute_id: '' });
      showSnackbar('Institute manager created successfully');
      fetchInstituteManagers();
      fetchDashboardStats();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create institute manager', 'error');
    }
  };

  const handleAssignInstituteManager = async () => {
    try {
      await api.put(`/sales/institute-managers/${selectedInstituteManager.id}/assign`, {
        institute_id: selectedAssignInstitute?.id || null,
      });
      setAssignInstituteManagerOpen(false);
      setSelectedInstituteManager(null);
      setSelectedAssignInstitute(null);
      showSnackbar('Institute manager assignment updated');
      fetchInstituteManagers();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to assign institute manager', 'error');
    }
  };

  const handleMenuClick = (itemId) => {
    setCurrentView(itemId);
    if (isMobile) setSidebarOpen(false);
  };

  // Render Dashboard
  const renderDashboard = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4}>
        <Typography variant="h4" fontWeight="800" gutterBottom
          sx={{
            background: GRADIENTS.navyLight,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
          Welcome, {user?.name || 'Sales'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage libraries, institutes, and bookings from your dashboard
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : dashboardStats && (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              sx={{ borderRadius: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              <CardContent sx={{ color: 'white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" fontWeight="800">{dashboardStats.totalLibraries}</Typography>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Libraries</Typography>
                  </Box>
                  <Business sx={{ fontSize: 48, opacity: 0.5 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              sx={{ borderRadius: 4, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}
            >
              <CardContent sx={{ color: 'white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" fontWeight="800">{dashboardStats.totalLibrarians}</Typography>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Librarians</Typography>
                  </Box>
                  <Person sx={{ fontSize: 48, opacity: 0.5 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              sx={{ borderRadius: 4, background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)' }}
            >
              <CardContent sx={{ color: 'white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" fontWeight="800">{dashboardStats.totalInstitutes}</Typography>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Institutes</Typography>
                  </Box>
                  <School sx={{ fontSize: 48, opacity: 0.5 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              sx={{ borderRadius: 4, background: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)' }}
            >
              <CardContent sx={{ color: 'white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" fontWeight="800">{dashboardStats.totalBookings}</Typography>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Bookings</Typography>
                  </Box>
                  <Event sx={{ fontSize: 48, opacity: 0.5 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12}>
            <MotionPaper
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              sx={{ p: 3, borderRadius: 4 }}
            >
              <Typography variant="h6" fontWeight="700" gutterBottom>Quick Actions</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Business />}
                    onClick={() => { setCurrentView('libraries'); setCreateLibraryOpen(true); }}
                    sx={{ py: 2, borderRadius: 3 }}
                  >
                    Add Library
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PersonAdd />}
                    onClick={() => { setCurrentView('librarians'); setCreateLibrarianOpen(true); }}
                    sx={{ py: 2, borderRadius: 3 }}
                  >
                    Add Librarian
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<School />}
                    onClick={() => { setCurrentView('institutes'); setCreateInstituteOpen(true); }}
                    sx={{ py: 2, borderRadius: 3 }}
                  >
                    Add Institute
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AdminPanelSettings />}
                    onClick={() => { setCurrentView('institute-managers'); setCreateInstituteManagerOpen(true); }}
                    sx={{ py: 2, borderRadius: 3 }}
                  >
                    Add Manager
                  </Button>
                </Grid>
              </Grid>
            </MotionPaper>
          </Grid>
        </Grid>
      )}
    </motion.div>
  );

  // Render Libraries
  const renderLibraries = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom
            sx={{
              background: GRADIENTS.navyLight,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Library Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage libraries ({libraryTotal} total)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateLibraryOpen(true)}
          sx={{ borderRadius: 3, background: GRADIENTS.brand, px: 3 }}
        >
          Add Library
        </Button>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ p: 3, mb: 3, borderRadius: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search libraries..."
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchLibraries()}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button fullWidth variant="contained" onClick={() => { setLibraryPage(0); fetchLibraries(); }} sx={{ borderRadius: 2 }}>
              Search
            </Button>
          </Grid>
        </Grid>
      </MotionPaper>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {libraryLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell><strong>Library</strong></TableCell>
                    <TableCell><strong>Address</strong></TableCell>
                    <TableCell><strong>City</strong></TableCell>
                    <TableCell><strong>Capacity</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {libraries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No libraries found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    libraries.map((library) => (
                      <TableRow key={library.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                              {library.name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography fontWeight="600">{library.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{library.address}</TableCell>
                        <TableCell>{library.city || 'N/A'}</TableCell>
                        <TableCell>{library.capacity || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={library.is_active ? 'Active' : 'Inactive'}
                            color={library.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditLibraryDialog(library)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={libraryTotal}
              rowsPerPage={libraryRowsPerPage}
              page={libraryPage}
              onPageChange={(e, newPage) => setLibraryPage(newPage)}
              onRowsPerPageChange={(e) => { setLibraryRowsPerPage(parseInt(e.target.value, 10)); setLibraryPage(0); }}
            />
          </>
        )}
      </MotionPaper>
    </motion.div>
  );

  // Render Librarians
  const renderLibrarians = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom
            sx={{
              background: GRADIENTS.navyLight,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Librarian Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and assign librarians to libraries
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setCreateLibrarianOpen(true)}
          sx={{ borderRadius: 3, background: GRADIENTS.brand, px: 3 }}
        >
          Add Librarian
        </Button>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {librarianLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell><strong>Librarian</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Phone</strong></TableCell>
                  <TableCell><strong>Assigned Library</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {librarians.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No librarians found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  librarians.map((librarian) => (
                    <TableRow key={librarian.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                            {librarian.name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography fontWeight="600">{librarian.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{librarian.email}</TableCell>
                      <TableCell>{librarian.phone || 'N/A'}</TableCell>
                      <TableCell>
                        {librarian.library ? (
                          <Chip label={librarian.library.name} color="primary" size="small" variant="outlined" />
                        ) : (
                          <Chip label="Not Assigned" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={librarian.blocked ? 'Blocked' : 'Active'}
                          color={librarian.blocked ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Assign Library">
                          <IconButton
                            size="small"
                            onClick={() => { setSelectedLibrarian(librarian); setAssignLibrarianOpen(true); }}
                          >
                            <Business fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MotionPaper>
    </motion.div>
  );

  // Render Institutes
  const renderInstitutes = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom
            sx={{
              background: GRADIENTS.navyLight,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Institute Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage institutes ({instituteTotal} total)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateInstituteOpen(true)}
          sx={{ borderRadius: 3, background: GRADIENTS.brand, px: 3 }}
        >
          Add Institute
        </Button>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ p: 3, mb: 3, borderRadius: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search institutes..."
              value={instituteSearch}
              onChange={(e) => setInstituteSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchInstitutes()}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button fullWidth variant="contained" onClick={() => { setInstitutePage(0); fetchInstitutes(); }} sx={{ borderRadius: 2 }}>
              Search
            </Button>
          </Grid>
        </Grid>
      </MotionPaper>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {instituteLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell><strong>Institute</strong></TableCell>
                    <TableCell><strong>Address</strong></TableCell>
                    <TableCell><strong>City</strong></TableCell>
                    <TableCell><strong>Contact Email</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {institutes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No institutes found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    institutes.map((institute) => (
                      <TableRow key={institute.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)' }}>
                              {institute.name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography fontWeight="600">{institute.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{institute.address}</TableCell>
                        <TableCell>{institute.city || 'N/A'}</TableCell>
                        <TableCell>{institute.contact_email || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={institute.is_active ? 'Active' : 'Inactive'}
                            color={institute.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditInstituteDialog(institute)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={instituteTotal}
              rowsPerPage={instituteRowsPerPage}
              page={institutePage}
              onPageChange={(e, newPage) => setInstitutePage(newPage)}
              onRowsPerPageChange={(e) => { setInstituteRowsPerPage(parseInt(e.target.value, 10)); setInstitutePage(0); }}
            />
          </>
        )}
      </MotionPaper>
    </motion.div>
  );

  // Render Institute Managers
  const renderInstituteManagers = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom
            sx={{
              background: GRADIENTS.navyLight,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            Institute Manager Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and assign institute managers
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setCreateInstituteManagerOpen(true)}
          sx={{ borderRadius: 3, background: GRADIENTS.brand, px: 3 }}
        >
          Add Manager
        </Button>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {instituteManagerLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell><strong>Manager</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Phone</strong></TableCell>
                  <TableCell><strong>Assigned Institute</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {instituteManagers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No institute managers found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  instituteManagers.map((manager) => (
                    <TableRow key={manager.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ background: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)' }}>
                            {manager.name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography fontWeight="600">{manager.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{manager.email}</TableCell>
                      <TableCell>{manager.phone || 'N/A'}</TableCell>
                      <TableCell>
                        {manager.institute ? (
                          <Chip label={manager.institute.name} color="primary" size="small" variant="outlined" />
                        ) : (
                          <Chip label="Not Assigned" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={manager.blocked ? 'Blocked' : 'Active'}
                          color={manager.blocked ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Assign Institute">
                          <IconButton
                            size="small"
                            onClick={() => { setSelectedInstituteManager(manager); setAssignInstituteManagerOpen(true); }}
                          >
                            <School fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MotionPaper>
    </motion.div>
  );

  // Render Bookings
  const renderBookings = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4}>
        <Typography variant="h4" fontWeight="800" gutterBottom
          sx={{
            background: GRADIENTS.navyLight,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
          Bookings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View all bookings ({bookingTotal} total)
        </Typography>
      </Box>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} sx={{ p: 3, mb: 3, borderRadius: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by student name..."
              value={bookingSearch}
              onChange={(e) => setBookingSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchBookings()}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={bookingStatus} label="Status" onChange={(e) => setBookingStatus(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <Button fullWidth variant="contained" onClick={() => { setBookingPage(0); fetchBookings(); }} sx={{ borderRadius: 2 }}>
              Search
            </Button>
          </Grid>
        </Grid>
      </MotionPaper>

      <MotionPaper initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {bookingLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell><strong>Student</strong></TableCell>
                    <TableCell><strong>Library</strong></TableCell>
                    <TableCell><strong>Plan</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Payment</strong></TableCell>
                    <TableCell><strong>Start Date</strong></TableCell>
                    <TableCell><strong>End Date</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No bookings found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bookings.map((booking) => (
                      <TableRow key={booking.id} hover>
                        <TableCell>{booking.student?.name || 'N/A'}</TableCell>
                        <TableCell>{booking.library?.name || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip label={booking.plan?.plan_type || 'N/A'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={booking.status}
                            size="small"
                            color={
                              booking.status === 'active' ? 'success' :
                              booking.status === 'pending' ? 'warning' :
                              booking.status === 'completed' ? 'info' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={booking.payment_status}
                            size="small"
                            color={booking.payment_status === 'paid' ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>{booking.start_date ? new Date(booking.start_date).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>{booking.end_date ? new Date(booking.end_date).toLocaleDateString() : 'N/A'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={bookingTotal}
              rowsPerPage={bookingRowsPerPage}
              page={bookingPage}
              onPageChange={(e, newPage) => setBookingPage(newPage)}
              onRowsPerPageChange={(e) => { setBookingRowsPerPage(parseInt(e.target.value, 10)); setBookingPage(0); }}
            />
          </>
        )}
      </MotionPaper>
    </motion.div>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard': return renderDashboard();
      case 'libraries': return renderLibraries();
      case 'librarians': return renderLibrarians();
      case 'institutes': return renderInstitutes();
      case 'institute-managers': return renderInstituteManagers();
      case 'bookings': return renderBookings();
      default: return renderDashboard();
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'transparent' }}>
      <CssBaseline />

      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...auroraTopbarSx(theme),
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={() => setSidebarOpen(!sidebarOpen)} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" fontWeight="700" sx={{ flexGrow: 1 }}>
            Sales Portal
          </Typography>
          <ThemeToggle sx={{ mr: 1 }} />
          <Tooltip title="Account">
            <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)}>
              <AccountCircle />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
            PaperProps={{ sx: { borderRadius: 2, minWidth: 200 } }}
          >
            <MenuItem onClick={() => setUserMenuAnchor(null)}>
              <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
              <ListItemText>{user?.name || 'Sales User'}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/login'); setTimeout(() => logout(), 0); }}>
              <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
            background: GRADIENTS.navyLight,
            color: 'white',
          },
        }}
      >
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Avatar sx={{ background: GRADIENTS.brand, width: 48, height: 48 }}>
            <Sell />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="700">Sales Portal</Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>{user?.name || 'Sales User'}</Typography>
          </Box>
        </Box>

        <List sx={{ px: 2, py: 1 }}>
          {menuSections.map((section) => (
            <React.Fragment key={section.id}>
              {section.label ? (
                <>
                  <ListItemButton
                    onClick={() => toggleSection(section.id)}
                    sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                  >
                    <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText primary={section.label} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }} />
                    {expandedSections[section.id] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={expandedSections[section.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {section.items.map((item) => (
                        <ListItemButton
                          key={item.id}
                          selected={currentView === item.id}
                          onClick={() => handleMenuClick(item.id)}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            ml: 2,
                            '&.Mui-selected': { bgcolor: 'rgba(242, 101, 34, 0.3)', '&:hover': { bgcolor: 'rgba(242, 101, 34, 0.4)' } },
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                          }}
                        >
                          <ListItemIcon sx={{ color: currentView === item.id ? 'primary.main' : 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                section.items.map((item) => (
                  <ListItemButton
                    key={item.id}
                    selected={currentView === item.id}
                    onClick={() => handleMenuClick(item.id)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      '&.Mui-selected': { bgcolor: 'rgba(242, 101, 34, 0.3)', '&:hover': { bgcolor: 'rgba(242, 101, 34, 0.4)' } },
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    <ListItemIcon sx={{ color: currentView === item.id ? 'primary.main' : 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 600 }} />
                  </ListItemButton>
                ))
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: sidebarOpen ? 0 : `-${drawerWidth}px` },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Container maxWidth="xl">
          {renderContent()}
        </Container>
      </Box>

      {/* Dialogs */}
      {/* Create Library Dialog */}
      <Dialog open={createLibraryOpen} onClose={() => setCreateLibraryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Library</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Library Name *" value={newLibrary.name} onChange={(e) => setNewLibrary({ ...newLibrary, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address *" value={newLibrary.address} onChange={(e) => setNewLibrary({ ...newLibrary, address: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={INDIAN_CITIES}
                value={newLibrary.city}
                onChange={(e, value) => setNewLibrary({ ...newLibrary, city: value })}
                renderInput={(params) => <TextField {...params} label="City" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Capacity" type="number" value={newLibrary.capacity} onChange={(e) => setNewLibrary({ ...newLibrary, capacity: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={newLibrary.website_enabled} onChange={(e) => setNewLibrary({ ...newLibrary, website_enabled: e.target.checked })} />}
                label="Enable Website Listing"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLibraryOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateLibrary}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Library Dialog */}
      <Dialog open={editLibraryOpen} onClose={() => setEditLibraryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Library</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Library Name *" value={editLibraryData.name} onChange={(e) => setEditLibraryData({ ...editLibraryData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address *" value={editLibraryData.address} onChange={(e) => setEditLibraryData({ ...editLibraryData, address: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={INDIAN_CITIES}
                value={editLibraryData.city}
                onChange={(e, value) => setEditLibraryData({ ...editLibraryData, city: value })}
                renderInput={(params) => <TextField {...params} label="City" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Capacity" type="number" value={editLibraryData.capacity} onChange={(e) => setEditLibraryData({ ...editLibraryData, capacity: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={editLibraryData.website_enabled} onChange={(e) => setEditLibraryData({ ...editLibraryData, website_enabled: e.target.checked })} />}
                label="Enable Website Listing"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLibraryOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditLibrary}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create Librarian Dialog */}
      <Dialog open={createLibrarianOpen} onClose={() => setCreateLibrarianOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Librarian</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name *" value={newLibrarian.name} onChange={(e) => setNewLibrarian({ ...newLibrarian, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email *" type="email" value={newLibrarian.email} onChange={(e) => setNewLibrarian({ ...newLibrarian, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Password *" type="password" value={newLibrarian.password} onChange={(e) => setNewLibrarian({ ...newLibrarian, password: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Phone" value={newLibrarian.phone} onChange={(e) => setNewLibrarian({ ...newLibrarian, phone: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLibrarianOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateLibrarian}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Librarian Dialog */}
      <Dialog open={assignLibrarianOpen} onClose={() => setAssignLibrarianOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Librarian to Library</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Assigning: <strong>{selectedLibrarian?.name}</strong>
            </Typography>
            <Autocomplete
              options={librarySearchOptions}
              getOptionLabel={(option) => option.name || ''}
              value={selectedAssignLibrary}
              onChange={(e, value) => setSelectedAssignLibrary(value)}
              onInputChange={(e, value) => searchLibrariesForAssignment(value)}
              loading={librarySearchLoading}
              renderInput={(params) => <TextField {...params} label="Select Library" placeholder="Search libraries..." />}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignLibrarianOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignLibrarian}>
            {selectedAssignLibrary ? 'Assign' : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Institute Dialog */}
      <Dialog open={createInstituteOpen} onClose={() => setCreateInstituteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Institute</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Institute Name *" value={newInstitute.name} onChange={(e) => setNewInstitute({ ...newInstitute, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address *" value={newInstitute.address} onChange={(e) => setNewInstitute({ ...newInstitute, address: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={INDIAN_CITIES}
                value={newInstitute.city}
                onChange={(e, value) => setNewInstitute({ ...newInstitute, city: value })}
                renderInput={(params) => <TextField {...params} label="City" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Contact Email" type="email" value={newInstitute.contact_email} onChange={(e) => setNewInstitute({ ...newInstitute, contact_email: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateInstituteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInstitute}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Institute Dialog */}
      <Dialog open={editInstituteOpen} onClose={() => setEditInstituteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Institute</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Institute Name *" value={editInstituteData.name} onChange={(e) => setEditInstituteData({ ...editInstituteData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address *" value={editInstituteData.address} onChange={(e) => setEditInstituteData({ ...editInstituteData, address: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={INDIAN_CITIES}
                value={editInstituteData.city}
                onChange={(e, value) => setEditInstituteData({ ...editInstituteData, city: value })}
                renderInput={(params) => <TextField {...params} label="City" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Contact Email" type="email" value={editInstituteData.contact_email} onChange={(e) => setEditInstituteData({ ...editInstituteData, contact_email: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditInstituteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditInstitute}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create Institute Manager Dialog */}
      <Dialog open={createInstituteManagerOpen} onClose={() => setCreateInstituteManagerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Institute Manager</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name *" value={newInstituteManager.name} onChange={(e) => setNewInstituteManager({ ...newInstituteManager, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email *" type="email" value={newInstituteManager.email} onChange={(e) => setNewInstituteManager({ ...newInstituteManager, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Password *" type="password" value={newInstituteManager.password} onChange={(e) => setNewInstituteManager({ ...newInstituteManager, password: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Phone" value={newInstituteManager.phone} onChange={(e) => setNewInstituteManager({ ...newInstituteManager, phone: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={INDIAN_CITIES}
                value={newInstituteManager.city || null}
                onChange={(e, value) => setNewInstituteManager({ ...newInstituteManager, city: value || '' })}
                renderInput={(params) => <TextField {...params} label="City" />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateInstituteManagerOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInstituteManager}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Institute Manager Dialog */}
      <Dialog open={assignInstituteManagerOpen} onClose={() => setAssignInstituteManagerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Manager to Institute</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Assigning: <strong>{selectedInstituteManager?.name}</strong>
            </Typography>
            <Autocomplete
              options={instituteSearchOptions}
              getOptionLabel={(option) => option.name || ''}
              value={selectedAssignInstitute}
              onChange={(e, value) => setSelectedAssignInstitute(value)}
              onInputChange={(e, value) => searchInstitutesForAssignment(value)}
              loading={instituteSearchLoading}
              renderInput={(params) => <TextField {...params} label="Select Institute" placeholder="Search institutes..." />}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignInstituteManagerOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignInstituteManager}>
            {selectedAssignInstitute ? 'Assign' : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SalesDashboardPage;
